import { useState, useEffect } from 'react';
import intl from 'react-intl-universal';

import localData from 'utils/localData';
import api from 'utils/api';

// components and parts
import { PageLoader, PageError } from 'components';
import { TABLE_TYPE } from 'components/PayrollTable/constants';
import { getSummaryTableColumns, getSummaryTableData } from './summaryColumns';
import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API, KENSHIN_API } from 'shared/constants/apis';
import { getFirstDateOfTheSameMonth } from 'utils/dateTime';
import { useSnackbar } from 'notistack';
import PayrollTable from 'components/PayrollTable';
import {
  getDetailedTableData,
  getDetailedTableColumns,
} from './detailsColumns';
import { getKenshinTableData, getKenshinTableColumns } from './kenshinColumns';
import { Outlet, useNavigate } from 'react-router-dom';
import { updateMyWage, updateMyWageKenshin } from '../shared/api';
import StarLabel from 'components/PayrollTable/components/StarLabel';
import logger from 'utils/logger';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
import { isCurrentUserTaskManagerKenshin } from 'utils/privilege';

const log = logger.createLogger('DashboardMyWage');

const DashboardMyWage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const firstDate = getFirstDateOfTheSameMonth(currentDate);

  const [loaded, setLoaded] = useState(false);
  const [loadedKenshin, setLoadedKenshin] = useState(false);
  const [ifError, setIfError] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.WAGES_START_DATE);
    return saved ? new Date(saved) : firstDate;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.WAGES_END_DATE);
    return saved ? new Date(saved) : currentDate;
  });
  const [tableType, setTableType] = useState(TABLE_TYPE.SUMMARY);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(-1);
  const [selectedOrganizationName, setSelectedOrganizationName] = useState('');
  const [myWageData, setMyWageData] = useState([]);
  const [myWageDataKenshin, setMyWageDataKenshin] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [updateApi, setUpdateApi] = useState(null);
  const [refreshTable, setRefreshTable] = useState(0);
  const doesHaveKenshinPermission = isCurrentUserTaskManagerKenshin();

  const dstOrgId = localData.get(USER_DATA.CURRENT_ORGANIZATION_ID);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.WAGES_START_DATE,
      selectedStartDate.toISOString()
    );
  }, [selectedStartDate]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.WAGES_END_DATE,
      selectedEndDate.toISOString()
    );
  }, [selectedEndDate]);

  useEffect(() => {
    const apiVariables = {
      params: {
        organizationId: parseInt(dstOrgId),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    const kenshinApiVariables = {
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        username: localData.get(USER_DATA.USERNAME),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    api
      .get(DOLPHIN_API.MY_WAGE, apiVariables)
      .then(async (data) => {
        const { myWages, doctors } = data;
        const newMyWages = myWages.sort((wage1, wage2) =>
          wage1.id < wage2.id ? 1 : -1
        );
        setMyWageData(newMyWages);
        setDoctors(doctors);
        setLoaded(true);
      })
      .catch((error) => {
        log.error('error', error);
        log.error('Error fetching my wages');
        setIfError(true);
      });

    api
      .get(KENSHIN_API.MY_WAGE, kenshinApiVariables)
      .then(async (data) => {
        const { myWages } = data;
        const newMyWages = myWages.sort((wage1, wage2) =>
          wage1.id < wage2.id ? 1 : -1
        );
        setMyWageDataKenshin(newMyWages);
        setLoadedKenshin(true);
      })
      .catch((error) => {
        log.error('error', error);
        log.error('Error fetching kenshin my wages');
        setIfError(true);
      });
  }, [dstOrgId, selectedStartDate, selectedEndDate, refreshTable]);

  useEffect(() => {
    if (loaded && loadedKenshin) {
      let columns = [];
      let data = [];
      if (tableType === TABLE_TYPE.SUMMARY) {
        columns = getSummaryTableColumns(
          setSelectedOrganizationName,
          setSelectedOrganizationId,
          setTableType,
          myWageData,
          myWageDataKenshin,
          doesHaveKenshinPermission
        );
        data = getSummaryTableData(myWageData, myWageDataKenshin);
        setUpdateApi(null);
      } else if (tableType === TABLE_TYPE.DETAILED) {
        columns = getDetailedTableColumns(openSeimitsuIssue);
        data = getDetailedTableData(myWageData, selectedOrganizationId);
        setUpdateApi(() => updateMyWage);
      } else if (tableType === TABLE_TYPE.DETAILED_KENSHIN) {
        columns = getKenshinTableColumns(openKenshinIssue);
        data = getKenshinTableData(myWageDataKenshin, selectedOrganizationId);
        setUpdateApi(() => updateMyWageKenshin);
      }
      setTableData(data);
      setColumns(columns);
    }
  }, [
    loaded,
    loadedKenshin,
    tableType,
    myWageData,
    myWageDataKenshin,
    selectedOrganizationId,
  ]);

  if (!(loaded && loadedKenshin)) {
    return <PageLoader />;
  }

  if (ifError) {
    return <PageError />;
  }

  const refreshTableData = () => {
    if (selectedEndDate.toDateString() === new Date().toDateString()) {
      setSelectedEndDate(new Date());
    }
    setRefreshTable(refreshTable + 1);
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  const openSeimitsuIssue = (issueId) => {
    navigate(`/dashboard/my-wage/seimitsu/issues/${issueId}`);
  };

  const openKenshinIssue = (issueId) => {
    navigate(`/dashboard/my-wage/kenshin/issues/${issueId}`);
  };

  const StarLabelComponent = (table) => {
    return (
      <StarLabel
        updateBillStatus={updateApi}
        table={table}
        refreshTableData={refreshTableData}
      />
    );
  };

  return (
    <div>
      <PayrollTable
        columns={columns}
        data={tableData}
        tableTitle={intl.get('my_wage_text_paid_wage')}
        timeRangeStart={selectedStartDate}
        timeRangeEnd={selectedEndDate}
        updateTimeRangeStart={setSelectedStartDate}
        updateTimeRangeEnd={setSelectedEndDate}
        handleRefreshClick={refreshTableData}
        setTableType={setTableType}
        subtitle={selectedOrganizationName}
        tableType={tableType}
        multiSelectComponent={StarLabelComponent}
        enableRowSelection={tableType === TABLE_TYPE.SUMMARY ? false : true}
      />
      <Outlet context={{ doctors, updateLocalProjectIssues: () => {} }} />
    </div>
  );
};

export default DashboardMyWage;
